import { useEffect, useRef, useState } from 'react';
import store from '../../store/flowBuilderStore';
import { editorSnapshot } from './editorTools';

/** Purpose: Track scoped graph snapshots, undo/redo, and dirty-state guards. Why: Histories must not mix flows or record transient selection changes. */
export default function useEditorHistory(scope, readOnly) {
  const history = useRef({ past: [], future: [], current: '', saved: '', restoring: false });
  const [, refresh] = useState(0);
  useEffect(() => {
    const initial = editorSnapshot(store.getState());
    history.current = { past: [], future: [], current: initial, saved: initial, restoring: false };
    return store.subscribe((state, previous) => {
      const next = editorSnapshot(state);
      const h = history.current;
      if (state.editorLoadRevision !== previous.editorLoadRevision || (previous.loadingFlow && !state.loadingFlow)) {
        Object.assign(h, { past: [], future: [], current: next, saved: next });
      } else if (!state.loadingFlow && !h.restoring && next !== h.current) {
        // Coalesce consecutive typing/drag updates without losing the pre-edit state.
        if (!h.lastEdit || Date.now() - h.lastEdit > 500) h.past = [...h.past.slice(-49), h.current];
        h.lastEdit = Date.now();
        h.current = next;
        h.future = [];
      }
      refresh((value) => value + 1);
    });
  }, [scope]);
  /** Purpose: Restore a snapshot from the undo or redo stack. Why: The restoring flag prevents restoration from becoming a new edit. */
  const move = (redo) => {
    if (readOnly || store.getState().loadingFlow || store.getState().savingFlow) return;
    const h = history.current;
    const source = redo ? h.future : h.past;
    if (!source.length) return;
    (redo ? h.past : h.future).push(h.current);
    h.current = source.pop();
    h.restoring = true;
    store.setState({ ...JSON.parse(h.current), selectedNodeId: '', selectedEdgeId: '' });
    h.restoring = false;
    h.lastEdit = 0;
    refresh((value) => value + 1);
  };
  const dirty = history.current.current !== history.current.saved;
  useEffect(() => {
    if (!dirty) return undefined;
    /** Purpose: Warn before unloading a dirty editor. Why: Closing or reloading a tab would discard unsaved graph changes. */
    const unload = (event) => { event.preventDefault(); event.returnValue = ''; };
    /** Purpose: Confirm link navigation while the graph is dirty. Why: Client-side links need a guard in addition to browser unload. */
    const leave = (event) => {
      const link = event.target.closest?.('a[href]');
      if (link && !link.target && link.href !== window.location.href && !window.confirm('Leave without saving your flow changes?')) {
        event.preventDefault(); event.stopPropagation();
      }
    };
    window.addEventListener('beforeunload', unload);
    document.addEventListener('click', leave, true);
    return () => { window.removeEventListener('beforeunload', unload); document.removeEventListener('click', leave, true); };
  }, [dirty]);
  useEffect(() => {
    /** Purpose: Dispatch graph undo/redo outside editable controls. Why: Text inputs must retain their native editing shortcuts. */
    const keydown = (event) => {
      if (event.target.closest?.('input, textarea, select, [contenteditable="true"]')) return;
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'z') {
        event.preventDefault(); move(event.shiftKey);
      }
    };
    window.addEventListener('keydown', keydown);
    return () => window.removeEventListener('keydown', keydown);
  });
  return { dirty, /** Purpose: Restore the previous snapshot. Why: Toolbar and keyboard actions share restoration guards. */ undo: () => move(false), /** Purpose: Restore the next snapshot. Why: A reverted edit can be replayed without creating a history entry. */ redo: () => move(true), canUndo: !readOnly && history.current.past.length > 0, canRedo: !readOnly && history.current.future.length > 0 };
}
